import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from '../constants';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import Loader from '../components/Loader';

const Landing = lazy(() => import('../pages/Landing'));
const Login = lazy(() => import('../pages/Login'));
const Signup = lazy(() => import('../pages/Signup'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const SignupHorseOwner = lazy(() => import('../pages/SignupHorseOwner'));
const SignupRider = lazy(() => import('../pages/SignupRider'));
const HorseOwnerHome = lazy(() => import('../pages/HorseOwnerHome'));
const ProfilePage = lazy(() => import('../pages/horse-owner/ProfilePage'));
const MyHorsesPage = lazy(() => import('../pages/horse-owner/MyHorsesPage'));
const AddHorsePage = lazy(() => import('../pages/horse-owner/AddHorsePage'));
const HorseDetailPage = lazy(() => import('../pages/horse-owner/HorseDetailPage'));
const RiderWelcome = lazy(() => import('../pages/RiderWelcome'));
const HorseRiderHome = lazy(() => import('../pages/HorseRiderHome'));
const RiderHomePage = lazy(() => import('../pages/horse-rider/RiderHomePage'));
const BrowseHorsesPage = lazy(() => import('../pages/horse-rider/BrowseHorsesPage'));
const RiderHorseDetailPage = lazy(() => import('../pages/horse-rider/RiderHorseDetailPage'));
const MyEoisPage = lazy(() => import('../pages/horse-rider/MyEoisPage'));
const RiderProfilesPage = lazy(() => import('../pages/horse-rider/RiderProfilesPage'));
const NewRiderProfilePage = lazy(() => import('../pages/horse-rider/NewRiderProfilePage'));
const EditRiderProfilePage = lazy(() => import('../pages/horse-rider/EditRiderProfilePage'));
const BookingRequestsPage = lazy(() => import('../pages/BookingRequestsPage'));
const Home = lazy(() => import('../pages/Home'));
const AdminApprovalPage = lazy(() => import('../pages/AdminApprovalPage'));

// Marketing site — "The Riding Post" (public)
const MarketingHome = lazy(() => import('../pages/marketing/Home'));
const Learn = lazy(() => import('../pages/marketing/Learn'));
const LearnRidingBasics = lazy(() => import('../pages/marketing/LearnRidingBasics'));
const LearnHorseKnowledge = lazy(() => import('../pages/marketing/LearnHorseKnowledge'));
const LearnEquestrianTerms = lazy(() => import('../pages/marketing/LearnEquestrianTerms'));
const LearnParentGuides = lazy(() => import('../pages/marketing/LearnParentGuides'));
const LearnTackEquipment = lazy(() => import('../pages/marketing/LearnTackEquipment'));
const RideSchools = lazy(() => import('../pages/marketing/RideSchools'));
const RideProgrammes = lazy(() => import('../pages/marketing/RideProgrammes'));
const RideCamps = lazy(() => import('../pages/marketing/RideCamps'));
const SchoolProfile = lazy(() => import('../pages/marketing/SchoolProfile'));
const EventsIndia = lazy(() => import('../pages/marketing/EventsIndia'));
const EventsInternational = lazy(() => import('../pages/marketing/EventsInternational'));
const EventPage = lazy(() => import('../pages/marketing/EventPage'));
const Watch = lazy(() => import('../pages/marketing/Watch'));
const WatchEpisode = lazy(() => import('../pages/marketing/WatchEpisode'));
const WatchYoungRiders = lazy(() => import('../pages/marketing/WatchYoungRiders'));
const YoungRiderVideo = lazy(() => import('../pages/marketing/YoungRiderVideo'));
const WatchChampions = lazy(() => import('../pages/marketing/WatchChampions'));
const ChampionVideo = lazy(() => import('../pages/marketing/ChampionVideo'));
const WatchOriginals = lazy(() => import('../pages/marketing/WatchOriginals'));
const OriginalVideo = lazy(() => import('../pages/marketing/OriginalVideo'));
const Originals = lazy(() => import('../pages/marketing/Originals'));
const OriginalsPiece = lazy(() => import('../pages/marketing/OriginalsPiece'));
const CommunityStories = lazy(() => import('../pages/marketing/CommunityStories'));
const StoryDetail = lazy(() => import('../pages/marketing/StoryDetail'));
const CommunityRiders = lazy(() => import('../pages/marketing/CommunityRiders'));
const CommunityYoungRiders = lazy(() => import('../pages/marketing/CommunityYoungRiders'));
const CommunityAsk = lazy(() => import('../pages/marketing/CommunityAsk'));
const CommunitySubmit = lazy(() => import('../pages/marketing/CommunitySubmit'));
const RiderProfile = lazy(() => import('../pages/marketing/RiderProfile'));
const YoungRiderProfile = lazy(() => import('../pages/marketing/YoungRiderProfile'));
const Marketplace = lazy(() => import('../pages/marketing/Marketplace'));
const AccountFeed = lazy(() => import('../pages/marketing/AccountFeed'));
const AccountProfile = lazy(() => import('../pages/marketing/AccountProfile'));
const AccountSaved = lazy(() => import('../pages/marketing/AccountSaved'));

// CMS admin (gated by a separate fixed login)
const CmsAdmin = lazy(() => import('../pages/admin/CmsAdmin'));
const CmsLogin = lazy(() => import('../pages/admin/CmsLogin'));
const CmsProtected = lazy(() => import('../pages/admin/CmsProtected'));

function SuspenseWrapper({ children, theme }: { children: React.ReactNode; theme?: 'owner' | 'rider' }) {
  return (
    <Suspense fallback={<Loader theme={theme} />}>
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Marketing site — fully public, no auth guard (visible to everyone)
  { path: ROUTES.LANDING, element: <SuspenseWrapper><MarketingHome /></SuspenseWrapper> },
  { path: ROUTES.LEARN, element: <SuspenseWrapper><Learn /></SuspenseWrapper> },
  { path: ROUTES.LEARN_RIDING_BASICS, element: <SuspenseWrapper><LearnRidingBasics /></SuspenseWrapper> },
  { path: ROUTES.LEARN_HORSE_KNOWLEDGE, element: <SuspenseWrapper><LearnHorseKnowledge /></SuspenseWrapper> },
  { path: ROUTES.LEARN_EQUESTRIAN_TERMS, element: <SuspenseWrapper><LearnEquestrianTerms /></SuspenseWrapper> },
  { path: ROUTES.LEARN_PARENT_GUIDES, element: <SuspenseWrapper><LearnParentGuides /></SuspenseWrapper> },
  { path: ROUTES.LEARN_TACK_EQUIPMENT, element: <SuspenseWrapper><LearnTackEquipment /></SuspenseWrapper> },
  { path: ROUTES.RIDE_SCHOOLS, element: <SuspenseWrapper><RideSchools /></SuspenseWrapper> },
  { path: ROUTES.RIDE_PROGRAMMES, element: <SuspenseWrapper><RideProgrammes /></SuspenseWrapper> },
  { path: ROUTES.RIDE_CAMPS, element: <SuspenseWrapper><RideCamps /></SuspenseWrapper> },
  { path: ROUTES.SCHOOL_PROFILE, element: <SuspenseWrapper><SchoolProfile /></SuspenseWrapper> },
  { path: `${ROUTES.SCHOOL_PROFILE}/:id`, element: <SuspenseWrapper><SchoolProfile /></SuspenseWrapper> },
  { path: ROUTES.EVENTS_INDIA, element: <SuspenseWrapper><EventsIndia /></SuspenseWrapper> },
  { path: ROUTES.EVENTS_INTERNATIONAL, element: <SuspenseWrapper><EventsInternational /></SuspenseWrapper> },
  { path: ROUTES.EVENT_PAGE, element: <SuspenseWrapper><EventPage /></SuspenseWrapper> },
  { path: `${ROUTES.EVENT_PAGE}/:id`, element: <SuspenseWrapper><EventPage /></SuspenseWrapper> },
  { path: ROUTES.WATCH, element: <SuspenseWrapper><Watch /></SuspenseWrapper> },
  { path: ROUTES.WATCH_EPISODE, element: <SuspenseWrapper><WatchEpisode /></SuspenseWrapper> },
  { path: `${ROUTES.WATCH_EPISODE}/:id`, element: <SuspenseWrapper><WatchEpisode /></SuspenseWrapper> },
  { path: ROUTES.WATCH_YOUNG_RIDERS, element: <SuspenseWrapper><WatchYoungRiders /></SuspenseWrapper> },
  { path: `${ROUTES.WATCH_YOUNG_RIDERS}/:id`, element: <SuspenseWrapper><YoungRiderVideo /></SuspenseWrapper> },
  { path: ROUTES.WATCH_CHAMPIONS, element: <SuspenseWrapper><WatchChampions /></SuspenseWrapper> },
  { path: `${ROUTES.WATCH_CHAMPIONS}/:id`, element: <SuspenseWrapper><ChampionVideo /></SuspenseWrapper> },
  { path: ROUTES.WATCH_ORIGINALS, element: <SuspenseWrapper><WatchOriginals /></SuspenseWrapper> },
  { path: `${ROUTES.WATCH_ORIGINALS}/:id`, element: <SuspenseWrapper><OriginalVideo /></SuspenseWrapper> },
  { path: ROUTES.ORIGINALS, element: <SuspenseWrapper><Originals /></SuspenseWrapper> },
  { path: `${ROUTES.ORIGINALS}/:id`, element: <SuspenseWrapper><OriginalsPiece /></SuspenseWrapper> },
  { path: ROUTES.COMMUNITY_STORIES, element: <SuspenseWrapper><CommunityStories /></SuspenseWrapper> },
  { path: `${ROUTES.STORY_DETAIL}/:id`, element: <SuspenseWrapper><StoryDetail /></SuspenseWrapper> },
  { path: ROUTES.COMMUNITY_RIDERS, element: <SuspenseWrapper><CommunityRiders /></SuspenseWrapper> },
  { path: ROUTES.COMMUNITY_YOUNG_RIDERS, element: <SuspenseWrapper><CommunityYoungRiders /></SuspenseWrapper> },
  { path: ROUTES.COMMUNITY_ASK, element: <SuspenseWrapper><CommunityAsk /></SuspenseWrapper> },
  { path: ROUTES.COMMUNITY_SUBMIT, element: <SuspenseWrapper><CommunitySubmit /></SuspenseWrapper> },
  { path: ROUTES.RIDER_PROFILE, element: <SuspenseWrapper><RiderProfile /></SuspenseWrapper> },
  { path: `${ROUTES.RIDER_PROFILE}/:id`, element: <SuspenseWrapper><RiderProfile /></SuspenseWrapper> },
  { path: `${ROUTES.YOUNG_RIDER_PROFILE}/:id`, element: <SuspenseWrapper><YoungRiderProfile /></SuspenseWrapper> },
  { path: ROUTES.MARKETPLACE, element: <SuspenseWrapper><Marketplace /></SuspenseWrapper> },
  { path: ROUTES.ACCOUNT_FEED, element: <SuspenseWrapper><AccountFeed /></SuspenseWrapper> },
  { path: ROUTES.ACCOUNT_PROFILE, element: <SuspenseWrapper><AccountProfile /></SuspenseWrapper> },
  { path: ROUTES.ACCOUNT_SAVED, element: <SuspenseWrapper><AccountSaved /></SuspenseWrapper> },

  // CMS admin — gated by a separate fixed login (admin@theridingpost.com)
  { path: ROUTES.ADMIN_LOGIN, element: <SuspenseWrapper><CmsLogin /></SuspenseWrapper> },
  {
    path: ROUTES.ADMIN_CMS,
    element: (
      <SuspenseWrapper>
        <CmsProtected>
          <CmsAdmin />
        </CmsProtected>
      </SuspenseWrapper>
    ),
  },

  // Public app routes — auth pages + app landing, redirect to home if already logged in
  {
    element: <PublicRoute />,
    children: [
      { path: ROUTES.APP_HOME, element: <SuspenseWrapper><Landing /></SuspenseWrapper> },
      { path: ROUTES.LOGIN, element: <SuspenseWrapper><Login /></SuspenseWrapper> },
      { path: ROUTES.FORGOT_PASSWORD, element: <SuspenseWrapper><ForgotPassword /></SuspenseWrapper> },
      { path: ROUTES.SIGNUP, element: <SuspenseWrapper><Signup /></SuspenseWrapper> },
      { path: ROUTES.SIGNUP_HORSE_OWNER, element: <SuspenseWrapper><SignupHorseOwner /></SuspenseWrapper> },
      { path: ROUTES.SIGNUP_RIDER, element: <SuspenseWrapper><SignupRider /></SuspenseWrapper> },
      { path: ROUTES.RIDER_WELCOME, element: <SuspenseWrapper theme="rider"><RiderWelcome /></SuspenseWrapper> },
    ],
  },
  // Private routes — redirect to login if not authenticated
  {
    element: <PrivateRoute />,
    children: [
      { path: ROUTES.DASHBOARD, element: <SuspenseWrapper><Home /></SuspenseWrapper> },
      {
        path: ROUTES.HORSE_OWNER_HOME,
        element: <SuspenseWrapper><HorseOwnerHome /></SuspenseWrapper>,
        children: [
          { path: 'profile', element: <Suspense fallback={null}><ProfilePage /></Suspense> },
          { path: 'my-horses', element: <Suspense fallback={null}><MyHorsesPage /></Suspense> },
          { path: 'my-horses/:horseId', element: <Suspense fallback={null}><HorseDetailPage /></Suspense> },
          { path: 'add-a-horse', element: <Suspense fallback={null}><AddHorsePage /></Suspense> },
        ],
      },
      {
        path: ROUTES.HORSE_RIDER_HOME,
        element: <SuspenseWrapper theme="rider"><HorseRiderHome /></SuspenseWrapper>,
        children: [
          { index: true, element: <Suspense fallback={null}><RiderHomePage /></Suspense> },
          { path: 'browse-horses', element: <Suspense fallback={null}><BrowseHorsesPage /></Suspense> },
          { path: 'browse-horses/:horseId', element: <Suspense fallback={null}><RiderHorseDetailPage /></Suspense> },
          { path: 'my-eois', element: <Suspense fallback={null}><MyEoisPage /></Suspense> },
          { path: 'rider-profiles', element: <Suspense fallback={null}><RiderProfilesPage /></Suspense> },
          { path: 'rider-profiles/new', element: <Suspense fallback={null}><NewRiderProfilePage /></Suspense> },
          { path: 'rider-profiles/:profileId/edit', element: <Suspense fallback={null}><EditRiderProfilePage /></Suspense> },
        ],
      },
      { path: ROUTES.BOOKING_REQUESTS, element: <SuspenseWrapper><BookingRequestsPage /></SuspenseWrapper> },
      { path: ROUTES.ADMIN_APPROVAL, element: <SuspenseWrapper><AdminApprovalPage /></SuspenseWrapper> },
    ],
  },
  // Catch-all: redirect undefined routes to the marketing home
  {
    path: '*',
    element: <Navigate to={ROUTES.LANDING} replace />,
  },
]);
